import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Webhook, RefreshCw, CheckCircle2, AlertTriangle, Copy, Shield, Loader2, ExternalLink, Home } from "lucide-react";

const STATUS_STYLE = {
  ok: { bg: "#DCFCE7", fg: "#166534", label: "OK" },
  error: { bg: "#FEE2E2", fg: "#991B1B", label: "ERROR" },
  received: { bg: "#FEF3C7", fg: "#78350F", label: "RECEIVED" },
};

export default function AdminWebhooks() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const { data } = await api.get("/billing/admin/webhook-status");
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load webhook status");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return <div className="p-10 text-center text-[var(--ink-3)]" data-testid="admin-webhooks-loading">Loading webhook status…</div>;
  }
  if (!data) return null;

  const { secret_configured, secret_masked, endpoint_url_expected_by_stripe, recent_events, last_100_summary } = data;
  const total100 = (last_100_summary?.ok || 0) + (last_100_summary?.error || 0);
  const okRate = total100 > 0 ? Math.round(((last_100_summary?.ok || 0) / total100) * 100) : null;

  const copyUrl = () => {
    navigator.clipboard.writeText(endpoint_url_expected_by_stripe).then(() => toast.success("URL copied"));
  };

  return (
    <div className="space-y-6" data-testid="admin-webhooks-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Webhook size={20} className="text-[var(--gold,#F59E0B)]" />
            <h1 className="font-display font-extrabold text-3xl tracking-tight">Stripe Webhooks</h1>
          </div>
          <p className="text-sm text-[var(--ink-2)] max-w-2xl">
            Live health of the Stripe → iSpeedToClose event stream. If subscriptions ever stall, this is the first place to look.
          </p>
        </div>
        <button
          type="button"
          className="btn-ghost text-sm"
          onClick={() => load({ silent: true })}
          disabled={refreshing}
          data-testid="admin-webhooks-refresh"
        >
          {refreshing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Refresh
        </button>
      </div>

      {/* Configuration card */}
      <div className="rounded-xl border border-[var(--line)] bg-white p-6 shadow-sm space-y-4" data-testid="admin-webhooks-config">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Shield size={16} className="text-[var(--ink-2)]" />
            <span className="font-display font-bold text-lg">Configuration</span>
          </div>
          {secret_configured ? (
            <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded" style={{ background: "#DCFCE7", color: "#166534" }}>
              <CheckCircle2 size={12} />Secret configured
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded" style={{ background: "#FEE2E2", color: "#991B1B" }}>
              <AlertTriangle size={12} />No secret set
            </span>
          )}
        </div>

        <Row label="Endpoint URL Stripe should call">
          <div className="flex items-center gap-2 flex-wrap">
            <code className="text-xs bg-[#F1F5F9] px-2 py-1 rounded font-mono break-all">{endpoint_url_expected_by_stripe}</code>
            <button type="button" onClick={copyUrl} className="text-xs text-[var(--ink-2)] hover:text-[var(--ink)] flex items-center gap-1" data-testid="admin-webhooks-copy-url">
              <Copy size={12} />Copy
            </button>
            <a
              href="https://dashboard.stripe.com/webhooks"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-[var(--ink-2)] hover:text-[var(--ink)] flex items-center gap-1"
              data-testid="admin-webhooks-open-stripe"
            >
              Open Stripe Dashboard <ExternalLink size={11} />
            </a>
          </div>
        </Row>

        <Row label="Signing secret in server env">
          <code className="text-xs bg-[#F1F5F9] px-2 py-1 rounded font-mono" data-testid="admin-webhooks-secret-mask">
            {secret_masked || <span className="text-red-600">not set — Stripe events will fail</span>}
          </code>
          {!secret_configured && (
            <p className="text-xs text-red-700 mt-2 leading-relaxed">
              Set <code>STRIPE_WEBHOOK_SECRET</code> in your production env vars. Copy the &ldquo;Signing secret&rdquo; value from Stripe → Webhooks → your endpoint → Reveal signing secret.
            </p>
          )}
        </Row>
      </div>

      {/* Rollup card — last 100 events */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard label="Last 100 processed" value={total100} testid="admin-webhooks-total" />
        <MetricCard label="OK" value={last_100_summary?.ok || 0} accent="#166534" testid="admin-webhooks-ok" />
        <MetricCard label="Errors" value={last_100_summary?.error || 0} accent={last_100_summary?.error ? "#991B1B" : "#94A3B8"} testid="admin-webhooks-errors" />
        <MetricCard label="Success rate" value={okRate == null ? "—" : `${okRate}%`} accent={okRate === 100 ? "#166534" : okRate == null || okRate >= 90 ? "#78350F" : "#991B1B"} testid="admin-webhooks-rate" />
      </div>

      {last_100_summary?.last_error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800" data-testid="admin-webhooks-last-error">
          <div className="font-semibold mb-1 flex items-center gap-1"><AlertTriangle size={14} />Last error</div>
          <code className="text-xs font-mono break-all">{last_100_summary.last_error}</code>
        </div>
      )}

      {/* RentCast API budget card */}
      <RentCastUsageCard />

      {/* Recent events table */}
      <div>
        <div className="text-xs uppercase tracking-widest text-[var(--ink-3)] mb-2">Recent events (last 20)</div>
        {(!recent_events || recent_events.length === 0) ? (
          <div className="rounded-lg border border-dashed border-[var(--line)] p-8 text-center text-sm text-[var(--ink-3)]" data-testid="admin-webhooks-empty">
            <Webhook size={28} className="mx-auto mb-2" />
            No webhook events recorded yet. Once Stripe sends its first event, it&apos;ll show up here.
          </div>
        ) : (
          <div className="rounded-lg border border-[var(--line)] bg-white overflow-hidden" data-testid="admin-webhooks-events-list">
            <table className="w-full text-sm">
              <thead className="bg-[#F8FAFC] text-[var(--ink-3)] text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold">Received</th>
                  <th className="text-left px-4 py-2 font-semibold">Type</th>
                  <th className="text-left px-4 py-2 font-semibold">Status</th>
                  <th className="text-left px-4 py-2 font-semibold">Event ID / Error</th>
                </tr>
              </thead>
              <tbody>
                {recent_events.map((e, i) => {
                  const s = STATUS_STYLE[e.status] || STATUS_STYLE.received;
                  return (
                    <tr key={e.event_id || i} className="border-t border-[var(--line)]" data-testid={`admin-webhooks-event-${i}`}>
                      <td className="px-4 py-2 whitespace-nowrap text-xs text-[var(--ink-2)]">
                        {formatWhen(e.received_at)}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-xs font-mono">
                        {e.event_type || <span className="text-[var(--ink-3)]">—</span>}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <span
                          className="inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                          style={{ background: s.bg, color: s.fg }}
                        >
                          {s.label}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs font-mono text-[var(--ink-2)] break-all max-w-[420px]">
                        {e.status === "error"
                          ? <span className="text-red-700">{e.error || "—"}</span>
                          : (e.event_id || "—")}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="text-xs text-[var(--ink-3)] rounded-lg bg-[#F8FAFC] border border-[var(--line)] p-4 leading-relaxed">
        <strong>Tip:</strong> To retry failed events, open the Stripe Dashboard → Webhooks → your endpoint. Each failed event has a &ldquo;Resend&rdquo; button. After a config fix, resending confirms the pipeline is healthy end-to-end.
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3)] mb-1">{label}</div>
      {children}
    </div>
  );
}

function MetricCard({ label, value, accent, testid }) {
  return (
    <div className="rounded-lg border border-[var(--line)] bg-white p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3)] mb-1">{label}</div>
      <div className="font-display font-black text-2xl" style={accent ? { color: accent } : undefined}>{value}</div>
    </div>
  );
}

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}


/**
 * RentCast API budget widget.
 *
 * Reads /api/admin/rentcast-usage and shows current-period call count, remaining
 * quota, and top endpoints so admins can spot where RentCast credits are going
 * BEFORE the "85% used" email arrives. Refreshes lazily on mount.
 */
function RentCastUsageCard() {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const { data } = await api.get("/admin/rentcast-usage");
      setUsage(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load RentCast usage");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="rounded-lg border border-[var(--line)] bg-white p-4 text-sm text-[var(--ink-3)]" data-testid="admin-rentcast-loading">
        Loading RentCast usage…
      </div>
    );
  }
  if (!usage) return null;

  const pct = usage.usage_pct || 0;
  const critical = pct >= 95;
  const warn = pct >= 80 && pct < 95;
  const okBg = critical ? "#FEE2E2" : warn ? "#FEF3C7" : "#DCFCE7";
  const okFg = critical ? "#991B1B" : warn ? "#78350F" : "#166534";
  const barColor = critical ? "#DC2626" : warn ? "#D97706" : "#059669";
  const modeLabel = usage.mode === "hard_stop" ? "HARD STOP" : usage.mode === "off" ? "TRACKING OFF" : "WARN";
  const topEndpoints = Object.entries(usage.by_endpoint || {})
    .map(([k, v]) => ({ endpoint: k.replace(/_/g, "."), count: v }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  return (
    <div className="rounded-lg border border-[var(--line)] bg-white p-4 space-y-3" data-testid="admin-rentcast-card">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Home size={16} className="text-[var(--primary)]" />
          <span className="font-semibold text-sm">RentCast API budget</span>
          <span
            className="text-[10px] font-bold px-1.5 py-0.5 rounded"
            style={{ background: okBg, color: okFg }}
            data-testid="admin-rentcast-mode"
          >
            {modeLabel}
          </span>
        </div>
        <button
          type="button"
          onClick={() => load({ silent: true })}
          disabled={refreshing}
          className="btn-ghost text-xs"
          data-testid="admin-rentcast-refresh"
        >
          {refreshing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard label="Calls this period" value={usage.total_calls} accent={okFg} testid="admin-rentcast-total" />
        <MetricCard label="Remaining" value={usage.remaining} accent={okFg} testid="admin-rentcast-remaining" />
        <MetricCard label="Monthly limit" value={usage.limit} testid="admin-rentcast-limit" />
        <MetricCard label="Cache hits" value={usage.cache_hits} accent="#166534" testid="admin-rentcast-cache" />
      </div>

      <div>
        <div className="flex items-center justify-between text-[11px] text-[var(--ink-2)] mb-1">
          <span>Period: {usage.period} {usage.billing_anchor_day > 1 ? `(rolls on day ${usage.billing_anchor_day})` : ""}</span>
          <span className="font-semibold" style={{ color: okFg }} data-testid="admin-rentcast-pct">{pct.toFixed(1)}% used</span>
        </div>
        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
          <div
            className="h-full transition-all"
            style={{ width: `${Math.min(100, pct)}%`, background: barColor }}
            data-testid="admin-rentcast-bar"
          />
        </div>
        {usage.cache_hit_ratio > 0 && (
          <div className="text-[10px] text-[var(--ink-3)] mt-1">
            Cache hit ratio: <b>{usage.cache_hit_ratio}%</b> ({usage.cache_hits} hits saved)
          </div>
        )}
      </div>

      {critical && (
        <div className="rounded-md bg-red-50 border border-red-200 p-2 text-[11px] text-red-800 flex items-start gap-1.5">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          <span>
            <b>Critical — {pct.toFixed(0)}% used.</b> Set <code>RENTCAST_BUDGET_MODE=hard_stop</code> in your
            production env vars to auto-halt further calls, or upgrade the RentCast plan.
          </span>
        </div>
      )}
      {warn && !critical && (
        <div className="rounded-md bg-amber-50 border border-amber-200 p-2 text-[11px] text-amber-800 flex items-start gap-1.5">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          <span><b>Warning — {pct.toFixed(0)}% used.</b> Watch the top endpoints below.</span>
        </div>
      )}

      {topEndpoints.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3)] mb-1">Top endpoints (uncached)</div>
          <ul className="space-y-1" data-testid="admin-rentcast-top-endpoints">
            {topEndpoints.map(({ endpoint, count }) => (
              <li key={endpoint} className="flex justify-between text-xs">
                <code className="text-[var(--ink-2)]">{endpoint}</code>
                <span className="font-semibold">{count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {usage.last_call_at && (
        <div className="text-[10px] text-[var(--ink-3)]">Last uncached call: {formatWhen(usage.last_call_at)}</div>
      )}
    </div>
  );
}
